import { LUDUM_USER_STATUSES, LUDUM_SPORTS } from '@/constants';
const { ROWING, PADDLING } = LUDUM_SPORTS;
const { LEFT } = LUDUM_USER_STATUSES;
import { isAthlete, isCoach } from '@/helpers/rolesHelper';
import get from 'get-object-path';

export const clubHelper = {
    namespaced: true,

    state: {
        me: null,
        users: [],
        allUsersLoaded: false,
        squads: [],
        eventTags: [],
        focuses: [],
        intensities: [],
        strokeRateIntensitiesPaddling: [],
        strokeRateIntensitiesRowing: [],
        heartRateIntensities: [],
        sessionTypes: [],
        unitsOfMeasurement: [],
        numberOfUnreadNotifications: 0,
        googleMapsLoaded: false
    },

    getters: {
        allUsersLoaded(state) {
            return state.allUsersLoaded;
        },

        me(state) {
            return state.me;
        },

        clubUsers(state) {
            return _.sortBy(
                state.users.filter(user => get(user, 'current_club_user.data.status.id') !== LEFT),
                'full_name'
            );
        },

        clubAthletes(state, getters) {
            return getters.clubUsers.filter(user =>
                (get(user, 'current_club_roles.data') || []).some(({ id }) => isAthlete(id))
            );
        },

        clubCoaches(state, getters) {
            return getters.clubUsers.filter(user =>
                (get(user, 'current_club_roles.data') || []).some(({ id }) => isCoach(id))
            );
        },

        clubSquads(state) {
            return _.sortBy(state.squads, ({ name }) => name.toLowerCase());
        },

        clubEventTags(state) {
            return _.sortBy(state.eventTags, 'name');
        },

        clubFocuses(state) {
            return _.sortBy(state.focuses, 'name');
        },

        clubSpeedIntensities(state) {
            return _.sortBy(state.intensities, 'order');
        },

        clubStrokeRateIntensitiesPaddling(state) {
            return state.strokeRateIntensitiesPaddling;
        },

        clubStrokeRateIntensitiesRowing(state) {
            return state.strokeRateIntensitiesRowing;
        },

        clubHeartRateIntensities(state) {
            return state.heartRateIntensities;
        },

        clubSessionTypes(state) {
            return _.sortBy(state.sessionTypes, 'name');
        },

        mostPopularSessionTypes(state) {
            return _.orderBy(
                state.sessionTypes.filter(({ rank }) => rank),
                'rank',
                'desc'
            );
        },

        unitsOfMeasurement(state) {
            return _.sortBy(state.unitsOfMeasurement, 'id');
        },

        numberOfUnreadNotifications(state) {
            return state.numberOfUnreadNotifications;
        },

        googleMapsLoaded(state) {
            return state.googleMapsLoaded;
        }
    },

    mutations: {
        setAllUsersLoaded(state, data) {
            state.allUsersLoaded = data;
        },

        fillMe(state, data) {
            state.me = data;
        },

        resetUsers(state) {
            state.users = [];
        },

        fillUsers(state, users) {
            users.forEach(user => state.users.findIndex(({ id }) => id === user.id) === -1 && state.users.push(user));
        },

        fillSquads(state, data) {
            state.squads = data;
        },

        fillEventTags(state, data) {
            state.eventTags = data;
        },

        fillFocuses(state, data) {
            state.focuses = data;
        },

        fillIntensities(state, data) {
            state.intensities = data;
        },

        fillStrokeRateIntensitiesPaddling(state, data) {
            state.strokeRateIntensitiesPaddling = data;
        },

        fillStrokeRateIntensitiesRowing(state, data) {
            state.strokeRateIntensitiesRowing = data;
        },

        fillHeartRateIntensities(state, data) {
            state.heartRateIntensities = data;
        },

        fillSessionTypes(state, data) {
            state.sessionTypes = [...data];
        },

        fillUnitsOfMeasurement(state, data) {
            state.unitsOfMeasurement = [...data];
        },
        fillNumberOfUnreadNotifications(state, data) {
            state.numberOfUnreadNotifications = data;
        },
        updateGoogleMaps(state, data) {
            state.googleMapsLoaded = data;
        },

        resetStore(state) {
            state.users = [];
            state.allUsersLoaded = false;
            state.squads = [];
            state.eventTags = [];
            state.focuses = [];
            state.intensities = [];
            state.strokeRateIntensitiesPaddling = [];
            state.strokeRateIntensitiesRowing = [];
            state.heartRateIntensities = [];
            state.sessionTypes = [];
            state.unitsOfMeasurement = [];
            state.numberOfUnreadNotifications = 0;
        }
    },

    actions: {
        getMe(context) {
            return context
                .dispatch(
                    'get',
                    {
                        url:
                            api +
                            'me?fields=id|first_name|last_name|full_name|email|picture|is_admin|firebase_password|created_at'
                    },
                    { root: true }
                )
                .then(response => {
                    context.commit('fillMe', response.data);
                    context.dispatch('subscribeToUpdates', response.data, { root: true });
                    return response;
                });
        },

        getClubSquads(context) {
            return context
                .dispatch(
                    'get',
                    {
                        url: api + 'squads',
                        params: {
                            club_id: context.rootGetters.selectedClubId,
                            include: 'squad_users.user:fields(id|picture|first_name|last_name|full_name)',
                            limit: 1000 // limit agreed with Ivan
                        }
                    },
                    { root: true }
                )
                .then(response => {
                    context.commit('fillSquads', response.data.data);
                    return response;
                });
        },

        getClubUsers(context, payload) {
            const { page, clubId, status } = payload;
            return context
                .dispatch(
                    'get',
                    {
                        url:
                            window.api +
                            'club-users?fields=id|full_name|first_name|last_name|status|picture|date_of_birth|gender|user_groups',
                        params: {
                            club_id: clubId,
                            status,
                            page,
                            limit: 1000,
                            include:
                                'current_club_user:fields(cox|stroke|scull|sweep|status),' +
                                'last_user_stat:fields(healthy|ill|injured|date),crew_user_groups,user_squads.squad:fields(id|name),current_club_roles'
                        }
                    },
                    { root: true }
                )
                .then(results => {
                    return results;
                });
        },

        getSpeedIntensities(context, payload = { force: false, requires: false }) {
            if ((context.getters.clubSpeedIntensities.length || !payload.requires) && !payload.force) return;
            return context
                .dispatch(
                    'get',
                    {
                        url: `${api}intensities`
                    },
                    {
                        root: true
                    }
                )
                .then(response => {
                    context.commit('fillIntensities', response.data);
                    context.dispatch('newSessionIntensities', response.data, { root: true });
                    return response;
                });
        },

        getFocuses(context, payload = {}) {
            if (context.getters.clubFocuses.length && !payload.force) return;
            return context
                .dispatch(
                    'get',
                    {
                        url: `${api}focuses`,
                        params: { sport: payload.sport }
                    },
                    { root: true }
                )
                .then(response => {
                    context.commit('fillFocuses', response.data);
                    return response;
                });
        },

        getStrokeRateIntensitiesRowing(context, { requires, force }) {
            if ((context.getters.clubStrokeRateIntensitiesRowing.length || !requires) && !force) return;
            return context.dispatch('getStrokeRateIntensities', ROWING);
        },

        getStrokeRateIntensitiesPaddling(context, { requires, force }) {
            if ((context.getters.clubStrokeRateIntensitiesPaddling.length || !requires) && !force) return;
            return context.dispatch('getStrokeRateIntensities', PADDLING);
        },

        getStrokeRateIntensities(context, sport) {
            context
                .dispatch(
                    'get',
                    {
                        url: `${api}stroke-rate-zones`,
                        params: { sport }
                    },
                    {
                        root: true
                    }
                )
                .then(response => {
                    if (sport === PADDLING) {
                        context.commit('fillStrokeRateIntensitiesPaddling', response.data);
                        context.dispatch('newSessionStrokeRateIntensitiesPaddling', response.data, { root: true });
                        return response;
                    } else if (sport === ROWING) {
                        context.commit('fillStrokeRateIntensitiesRowing', response.data);
                        context.dispatch('newSessionStrokeRateIntensitiesRowing', response.data, { root: true });
                        return response;
                    }
                });
        },

        getHeartRateIntensities(context, payload = {}) {
            if ((context.getters.clubHeartRateIntensities.length || !payload.requires) && !payload.force) return;
            context
                .dispatch(
                    'get',
                    {
                        url: `${api}heart-rate-zones`
                    },
                    {
                        root: true
                    }
                )
                .then(response => {
                    context.commit('fillHeartRateIntensities', response.data);
                    context.dispatch('newSessionHeartRateIntensities', response.data, { root: true });
                    return response;
                });
        },

        getUnitsOfMeasurement(context, payload = {}) {
            if (context.getters.unitsOfMeasurement.length && !payload.force) return;
            return context
                .dispatch(
                    'get',
                    {
                        url: `${api}units-of-measurement`
                    },
                    { root: true }
                )
                .then(response => {
                    context.commit('fillUnitsOfMeasurement', response.data);
                    return response;
                });
        },

        getNotifications(context, params) {
            return context.dispatch(
                'get',
                {
                    url: api + 'notifications',
                    params
                },
                { root: true }
            );
        },

        getNumberOfUnreadNotifications(context) {
            if (!context.rootGetters.selectedClubId) {
                return;
            }
            return context
                .dispatch(
                    'get',
                    {
                        url: api + 'notifications/number-of-unread',
                        params: {
                            club_id: context.rootGetters.selectedClubId
                        }
                    },
                    { root: true }
                )
                .then(response => {
                    context.commit('fillNumberOfUnreadNotifications', response.data.number_of_unread);
                    return response;
                });
        }
    }
};

export default clubHelper;
